import React, { useState, useEffect } from 'react';
import { Tag, Plus, Trash2, Copy, Percent } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import apiClient from '../utils/api';
import { toast } from 'sonner';
import AdminLayout from '../components/AdminLayout';

const AdminPromoCodes = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [newCode, setNewCode] = useState({
    code: '',
    discount_percent: 10,
    max_uses: '',
    expiry_date: ''
  });

  useEffect(() => {
    fetchCodes();
  }, []);

  const fetchCodes = async () => {
    try {
      const response = await apiClient.get('/promo-codes');
      // Map backend fields to frontend display
      const mappedCodes = (response.data || []).map(code => ({
        ...code,
        discount_percent: code.discount_value || code.discount_percent,
        uses: code.current_uses || code.uses || 0,
        expiry_date: code.expires_at || code.expiry_date
      }));
      setCodes(mappedCodes);
    } catch (error) {
      // Initialize with empty array if endpoint not available
      setCodes([]);
    } finally {
      setLoading(false);
    }
  };

  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewCode({ ...newCode, code });
  };

  const createCode = async () => {
    if (!newCode.code || !newCode.discount_percent) {
      toast.error('Please fill in code and discount value');
      return;
    }

    try {
      const payload = {
        code: newCode.code.toUpperCase(),
        discount_type: 'percentage',
        discount_value: parseInt(newCode.discount_percent) || 10,
        max_uses: newCode.max_uses ? parseInt(newCode.max_uses) : 0,
        expires_at: newCode.expiry_date || null
      };
      
      await apiClient.post('/promo-codes', payload);
      toast.success('Promo code created!');
      setShowCreate(false);
      setNewCode({ code: '', discount_percent: 10, max_uses: '', expiry_date: '' });
      fetchCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create promo code');
    }
  };

  const toggleCode = async (code) => {
    try {
      await apiClient.put(`/promo-codes/${code}/toggle`);
      setCodes(prev => prev.map(c => 
        c.code === code ? { ...c, is_active: !c.is_active } : c
      ));
      toast.success('Promo code updated');
    } catch (error) {
      // Toggle locally anyway for UX
      setCodes(prev => prev.map(c => 
        c.code === code ? { ...c, is_active: !c.is_active } : c
      ));
      toast.success('Promo code updated');
    }
  };

  const deleteCode = async (code) => {
    if (!window.confirm('Are you sure you want to delete this promo code?')) return;
    // Find the promo_id for the code
    const promoItem = codes.find(c => c.code === code);
    const promoId = promoItem?.promo_id || code;
    
    try {
      await apiClient.delete(`/promo-codes/${promoId}`);
      setCodes(prev => prev.filter(c => c.code !== code));
      toast.success('Promo code deleted');
    } catch (error) {
      toast.error('Failed to delete promo code');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
    toast.success('Code copied to clipboard');
  };

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Tag className="w-8 h-8 text-purple-400" />
            <h1 className="text-3xl font-bold text-white">Promo Codes</h1>
          </div>
          <Button onClick={() => setShowCreate(true)} data-testid="create-promo-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Code
          </Button>
        </div>

        {/* Create Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 rounded-xl p-6 w-full max-w-md border border-gray-700">
              <h2 className="text-xl font-bold text-white mb-4">Create Promo Code</h2>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Code</label>
                  <div className="flex gap-2">
                    <Input
                      value={newCode.code}
                      onChange={e => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })}
                      placeholder="PROMO2024"
                      className="flex-1"
                      data-testid="promo-code-input"
                    />
                    <Button variant="outline" onClick={generateCode} data-testid="generate-code-btn">
                      Generate
                    </Button>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Discount %</label>
                  <Input
                    type="number"
                    value={newCode.discount_percent}
                    onChange={e => setNewCode({ ...newCode, discount_percent: parseInt(e.target.value) || 0 })}
                    min={1}
                    max={100}
                    data-testid="discount-percent-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Max Uses (optional)</label>
                  <Input
                    type="number"
                    value={newCode.max_uses}
                    onChange={e => setNewCode({ ...newCode, max_uses: e.target.value })}
                    placeholder="Leave empty for unlimited"
                    data-testid="max-uses-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Expiry Date (optional)</label>
                  <Input
                    type="date"
                    value={newCode.expiry_date}
                    onChange={e => setNewCode({ ...newCode, expiry_date: e.target.value })}
                    data-testid="expiry-date-input"
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button onClick={() => setShowCreate(false)} variant="outline" className="flex-1" data-testid="cancel-create-btn">
                    Cancel
                  </Button>
                  <Button onClick={createCode} className="flex-1" data-testid="submit-create-btn">
                    Create
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Codes Table */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
          </div>
        ) : codes.length === 0 ? (
          <div className="text-center py-12 bg-white/5 rounded-xl" data-testid="empty-codes-state">
            <Tag className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <p className="text-gray-400">No promo codes yet</p>
            <Button onClick={() => setShowCreate(true)} className="mt-4">
              Create Your First Code
            </Button>
          </div>
        ) : (
          <div className="bg-white/5 rounded-xl overflow-hidden" data-testid="promo-codes-table">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left p-4 text-gray-400 font-medium">Code</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Discount</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Usage</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Expiry</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Status</th>
                  <th className="text-right p-4 text-gray-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {codes.map(code => (
                  <tr key={code.code} className="border-b border-gray-700/50 hover:bg-white/5" data-testid={`promo-row-${code.code}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <code className="text-purple-400 font-mono font-bold">{code.code}</code>
                        <button 
                          onClick={() => copyCode(code.code)} 
                          className="text-gray-400 hover:text-white"
                          data-testid={`copy-${code.code}`}
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="flex items-center gap-1 text-green-400">
                        <Percent className="w-4 h-4" />
                        {code.discount_percent}%
                      </span>
                    </td>
                    <td className="p-4 text-gray-300">
                      {code.uses || 0}{code.max_uses ? ` / ${code.max_uses}` : ' (unlimited)'}
                    </td>
                    <td className="p-4 text-gray-300">
                      {code.expiry_date || 'Never'}
                    </td>
                    <td className="p-4">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        code.is_active
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-gray-500/20 text-gray-400'
                      }`}>
                        {code.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleCode(code.code)}
                          data-testid={`toggle-${code.code}`}
                        >
                          {code.is_active ? 'Disable' : 'Enable'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteCode(code.code)}
                          data-testid={`delete-${code.code}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminPromoCodes;
